import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { LineChart, TrendingUp, TrendingDown, Minus, Plus, Trash2, Pencil, X, Power, Save } from 'lucide-react';

const RESULT_META = {
  profit: { label: 'Profit', color: '#22c55e', Icon: TrendingUp },
  loss: { label: 'Loss', color: '#ef4444', Icon: TrendingDown },
  neutral: { label: 'Neutral', color: 'var(--text-muted)', Icon: Minus }
};

const EMPTY_FORM = { date: '', pair: 'BTC/USDT', result: 'profit', profitPercent: '', totalAmount: '', notes: '' };

export default function AdminTrading() {
  const [settings, setSettings] = useState({ tradingActive: true, tradingDisplayText: '' });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const LIMIT = 15;

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchSettings = () => {
    setSettingsLoading(true);
    api.get('/trading/settings')
      .then(r => setSettings(r.data))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load trading settings'))
      .finally(() => setSettingsLoading(false));
  };

  const fetchLogs = useCallback(() => {
    setLoading(true);
    api.get(`/trading/logs?page=${page}&limit=${LIMIT}`)
      .then(r => { setLogs(r.data.logs); setTotal(r.data.total); })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load trading logs'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { fetchSettings(); }, []);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await api.put('/trading/settings', settings);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const editLog = (log) => {
    setEditingId(log._id);
    setForm({
      date: log.date ? log.date.slice(0, 10) : '',
      pair: log.pair,
      result: log.result,
      profitPercent: log.profitPercent,
      totalAmount: log.totalAmount,
      notes: log.notes || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitLog = async (e) => {
    e.preventDefault();
    if (!form.date) return toast.error('Date is required');

    setSubmitting(true);
    try {
      const payload = { ...form, profitPercent: Number(form.profitPercent) || 0, totalAmount: Number(form.totalAmount) || 0 };
      const res = editingId
        ? await api.put(`/trading/logs/${editingId}`, payload)
        : await api.post('/trading/logs', payload);
      toast.success(res.data.message);
      resetForm();
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save trading log');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Delete this trading log entry?')) return;
    try {
      await api.delete(`/trading/logs/${id}`);
      toast.success('Trading log deleted');
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <DashboardLayout>
      <h1 className="page-title">Trading Management</h1>
      <p className="page-subtitle">Control the Trading Dashboard users see, and log daily trading results for transparency</p>

      {/* Settings */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Power size={18} /> Trading Dashboard Settings</h3>
        </div>
        {settingsLoading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : (
          <form onSubmit={saveSettings}>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={settings.tradingActive}
                  onChange={e => setSettings(s => ({ ...s, tradingActive: e.target.checked }))}
                />
                Show Trading Dashboard to users
              </label>
            </div>
            <div className="form-group">
              <label>Display Text (shown to users on their Trading Dashboard)</label>
              <textarea
                className="form-control"
                rows={3}
                value={settings.tradingDisplayText}
                onChange={e => setSettings(s => ({ ...s, tradingDisplayText: e.target.value }))}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={savingSettings}>
              <Save size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        )}
      </div>

      {/* Add/Edit Log Form */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} /> {editingId ? 'Edit Trading Log' : 'Add Daily Trading Log'}
          </h3>
          {editingId && (
            <button className="btn btn-outline btn-sm" onClick={resetForm}><X size={14} /> Cancel Edit</button>
          )}
        </div>
        <form onSubmit={submitLog}>
          <div className="grid-4" style={{ gap: 16 }}>
            <div className="form-group">
              <label>Date</label>
              <input type="date" className="form-control" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Pair / Market</label>
              <input type="text" className="form-control" value={form.pair} onChange={e => setForm(f => ({ ...f, pair: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Result</label>
              <select className="form-control" value={form.result} onChange={e => setForm(f => ({ ...f, result: e.target.value }))}>
                <option value="profit">Profit</option>
                <option value="loss">Loss</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
            <div className="form-group">
              <label>Profit / Loss %</label>
              <input type="number" step="0.01" className="form-control" value={form.profitPercent} onChange={e => setForm(f => ({ ...f, profitPercent: e.target.value }))} />
            </div>
          </div>
          <div className="grid-2" style={{ gap: 16 }}>
            <div className="form-group">
              <label>Total Amount (₹)</label>
              <input type="number" className="form-control" value={form.totalAmount} onChange={e => setForm(f => ({ ...f, totalAmount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <input type="text" className="form-control" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional note shown with this entry" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : editingId ? 'Update Log' : 'Add Log'}
          </button>
        </form>
      </div>

      {/* Log History */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><LineChart size={18} /> Trading History</h3>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Total: {total} entries</div>
        </div>
        {loading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><LineChart size={40} /></div>
            <h3>No trading logs yet</h3>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead>
                <tr><th>Date</th><th>Pair</th><th>Result</th><th>%</th><th>Amount</th><th>Notes</th><th></th></tr>
              </thead>
              <tbody>
                {logs.map(log => {
                  const meta = RESULT_META[log.result] || RESULT_META.neutral;
                  return (
                    <tr key={log._id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.date).toLocaleDateString('en-IN')}</td>
                      <td>{log.pair}</td>
                      <td>
                        <span className="badge" style={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}40`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <meta.Icon size={12} /> {meta.label}
                        </span>
                      </td>
                      <td>{log.profitPercent}%</td>
                      <td>₹{Number(log.totalAmount).toLocaleString('en-IN')}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{log.notes || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline btn-sm" style={{ marginRight: 6, padding: 6 }} onClick={() => editLog(log)}><Pencil size={14} /></button>
                        <button className="btn btn-outline btn-sm" style={{ padding: 6, color: '#ef4444' }} onClick={() => deleteLog(log._id)}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const p = i + Math.max(1, page - 2);
              if (p > totalPages) return null;
              return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
            })}
            <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}